import { Lock, Check } from 'lucide-react';
import type { BadgeDefinition } from '@xdface-vault/shared';
import { getBadgeIcon } from './badge-icons.js';

interface BadgeCardProps {
  badge: BadgeDefinition;
  unlocked: boolean;
  unlockedAt?: string;
}

export function BadgeCard({ badge, unlocked, unlockedAt }: BadgeCardProps) {
  const BadgeIcon = getBadgeIcon(badge.id);
  return (
    <div
      className={[
        'group relative flex flex-col items-center rounded-2xl border p-6 text-center transition-all duration-200 card-hover',
        unlocked
          ? 'bg-surface border-border shadow-md'
          : 'bg-surface-2/50 border-border opacity-60',
      ].join(' ')}
    >
      {/* Icon Container - Using the brand gradient for unlocked */}
      <div className="relative mb-4">
        <div
          className={[
            'flex h-20 w-20 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110',
            unlocked
              ? 'bg-xd-action text-[var(--xds-fg)]'
              : 'bg-surface-2 text-text-subtle grayscale',
          ].join(' ')}
        >
          <span className="flex items-center justify-center" aria-hidden="true">
            <BadgeIcon className="h-9 w-9 drop-shadow-sm" />
          </span>
        </div>

        {/* Lock icon with system style */}
        {!unlocked && (
          <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-surface bg-surface text-text-subtle shadow-sm">
            <Lock className="h-3 w-3" />
          </div>
        )}
      </div>

      {/* Typography following system design hierarchy */}
      <div className="space-y-1">
        <span
          className={[
            'text-[10px] font-bold uppercase tracking-wider',
            unlocked ? 'text-xd-action-end' : 'text-text-muted',
          ].join(' ')}
        >
          {badge.category}
        </span>
        <h3 className="text-sm font-bold text-text">{badge.name}</h3>
        <p className="text-xs leading-tight text-text-muted line-clamp-2">{badge.description}</p>
      </div>

      {/* Unlock marker using system action color */}
      {unlocked && unlockedAt && (
        <div className="mt-4 flex items-center gap-1 text-[10px] font-bold text-xd-action-end uppercase tracking-tighter">
          <Check className="h-3 w-3" />
          <span>
            {new Date(unlockedAt).toLocaleDateString('es-AR', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        </div>
      )}
    </div>
  );
}
